import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  codesApi,
  type CodeInput,
  type CodeKind,
  type DispensaryCode,
  type ReaderTypeCode,
  type SensorTypeCode,
} from '../../lib/api/codes';
import styles from './CodeFormDialog.module.css';

/**
 * The add-code form — `POST /dispensary-codes`. There is no edit here:
 * `DispensaryCodeController` offers only `store()`/`destroy()` alongside
 * `index()`/`availability()`/`import()` (`codes.ts`'s own docblock), so a
 * code is created, deleted, or left exactly as it was.
 *
 * ## The legal `(kind, type_code)` pairing, enforced by what the Type select
 * actually offers
 *
 * `CodeInput` is a discriminated union on `kind` (`codes.ts`'s own
 * docblock) so a caller cannot even compile the illegal pairing the API's
 * own 422 refuses. This form mirrors that at the UI layer: `typeOptions`
 * below is derived from `kind`, not the full `CodeTypeCode` vocabulary, and
 * `type_code` resets to `''` whenever `kind` changes — a Reader form never
 * offers `libre_2_plus`/`libre_3_plus`, and a Sensor form never offers
 * `libre_3`.
 */

export interface CodeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (code: DispensaryCode) => void;
  /**
   * The code being corrected, or `undefined` to add a new one.
   *
   * ONE DIALOG FOR BOTH, and the fields are identical — a second component
   * would be this one with `create` swapped for `update`, which is how the
   * `(kind, type_code)` pairing rule above ends up implemented twice and
   * enforced in one of them.
   *
   * Only a FREE code is ever passed here: the server refuses to rewrite a
   * bound one with a `409`, and `CodeList` does not offer the button on a
   * bound row for the same reason it offers no delete there.
   */
  editing?: DispensaryCode;
}

const READER_TYPES: readonly ReaderTypeCode[] = ['libre_2', 'libre_3'];
const SENSOR_TYPES: readonly SensorTypeCode[] = ['libre_2', 'libre_2_plus', 'libre_3_plus'];

interface FormState {
  dispensaryId: string;
  kind: CodeKind;
  typeCode: string;
  code: string;
}

const BLANK: FormState = { dispensaryId: '', kind: 'reader', typeCode: '', code: '' };

const FORBIDDEN_CODE = 'forbidden';

function mapDetails(details: Readonly<Record<string, string[]>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details)) {
    const [first] = messages;
    if (first !== undefined) out[field] = first;
  }
  return out;
}

export function CodeFormDialog({ open, onClose, onSaved, editing }: CodeFormDialogProps) {
  const { t } = useTranslation();

  const [state, setState] = useState<FormState>(BLANK);
  const [missing, setMissing] = useState<readonly string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const dispensaryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Seeded from the row when correcting, blank when adding. `editing` is in
    // the dependency list so reopening the dialog on a DIFFERENT row does not
    // show the previous row's values — `open` alone stays `true` across that
    // change when the list swaps rows without closing.
    setState(
      editing === undefined
        ? BLANK
        : {
            dispensaryId: editing.dispensary?.uuid ?? '',
            kind: editing.kind,
            typeCode: editing.type_code,
            code: editing.code,
          },
    );
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, editing]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('codes.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('codes.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('codes.actionFailed');
    }
  }

  function handleKindChange(value: string): void {
    setState((current) => ({ ...current, kind: value as CodeKind, typeCode: '' }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: string[] = [];
    if (state.dispensaryId.trim() === '') empty.push('dispensary_id');
    if (state.typeCode === '') empty.push('type_code');
    if (state.code.trim() === '') empty.push('code');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const input: CodeInput =
        state.kind === 'reader'
          ? {
              dispensary_id: state.dispensaryId.trim(),
              code: state.code.trim(),
              kind: 'reader',
              type_code: state.typeCode as ReaderTypeCode,
            }
          : {
              dispensary_id: state.dispensaryId.trim(),
              code: state.code.trim(),
              kind: 'sensor',
              type_code: state.typeCode as SensorTypeCode,
            };
      const saved =
        editing === undefined
          ? await codesApi.create(input)
          : // The whole record, deliberately, even though every field is
            // optional on the wire: this form always shows all four, so all
            // four are what the operator just confirmed. Sending a computed
            // diff would make the request depend on a `before` this component
            // would then have to keep in step with the row underneath it.
            await codesApi.update(editing.uuid, input);
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const kindOptions: readonly SelectOption[] = [
    { value: 'reader', label: t('codes.kind.reader') },
    { value: 'sensor', label: t('codes.kind.sensor') },
  ];
  const typeChoices = state.kind === 'reader' ? READER_TYPES : SENSOR_TYPES;
  const typeOptions: readonly SelectOption[] = typeChoices.map((value) => ({
    value,
    label: t(`codes.type.${value}`),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      initialFocusRef={dispensaryRef}
      title={editing === undefined ? t('codes.createTitle') : t('codes.editTitle')}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div
          className={styles['failure']}
          role="alert"
          aria-atomic="true"
          data-testid="codeFormFailure"
        >
          {generalFailure}
        </div>

        <Input
          ref={dispensaryRef}
          label={t('codes.fieldDispensary')}
          width="fill"
          dir="ltr"
          required
          value={state.dispensaryId}
          error={
            missing.includes('dispensary_id') ? t('codes.required') : fieldErrors['dispensary_id']
          }
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, dispensaryId: value }));
          }}
        />

        <Select
          label={t('codes.fieldKind')}
          width="fill"
          required
          options={kindOptions}
          value={state.kind}
          error={fieldErrors['kind']}
          onChange={(event) => {
            handleKindChange(event.target.value);
          }}
        />

        <Select
          label={t('codes.fieldType')}
          width="fill"
          required
          placeholder={t('codes.anyType')}
          options={typeOptions}
          value={state.typeCode}
          error={missing.includes('type_code') ? t('codes.required') : fieldErrors['type_code']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, typeCode: value }));
          }}
        />

        <Input
          label={t('codes.fieldCode')}
          width="fill"
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={state.code}
          error={missing.includes('code') ? t('codes.required') : fieldErrors['code']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, code: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<FloppyDiskIcon size={16} />} type="submit" loading={submitting}>
            {t('codes.save')}
          </Button>
          <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onClose}>
            {t('codes.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
